import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ActivitySection from './ActivitySection';
import SectionState from './SectionState';
import LineChart from './charts/LineChart';
import { ExamStatTiles, ExamTable } from './ExamStats';
import {
  dayMonth, londonDateKey, modeLabel, paperKindLabel, paperScore, percent, plural, trimNumber, weekStart,
} from './lib/activityFormat';
import { paperRefFor } from './lib/paperReview';
import { paperReviewHref } from '../../../lib/dashboardParams';

const TIMED = new Set(['timed', 'timed_extra']);

/**
 * A link that opens the paper review in place (onOpenPaper) and still works
 * as a link: a router <Link> to the review's full URL (/teacher-dashboard?…),
 * so cmd-click, middle-click and "open in new tab" reach the review, not the
 * landing page a bare "?…" resolves to under the page's <base> tag. A
 * modified click is left to the browser; a plain one opens in place.
 */
const ReviewLink = ({ part, label, links, ariaLabel }) => {
  const ref = paperRefFor(part);
  return (
    <Link
      to={links.href(ref)}
      aria-label={ariaLabel}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        links.open(ref);
      }}
    >
      {label}
    </Link>
  );
};

const PaperRow = ({ paper, links }) => {
  const score = paperScore(paper.correct, paper.total, paper.answered);
  const meta = [paperKindLabel(paper.kind), modeLabel(paper.mode), score.detail].filter(Boolean).join(' · ');
  // A whole paper links from its name; a sitting of several parts links each part.
  const parts = links ? (paper.parts || []).filter((part) => paperRefFor(part)) : [];
  const single = parts.length === 1;
  return (
    <li className="td-act-rowline">
      <span className="td-act-when">{dayMonth(paper.completed_at)}</span>
      <div className="td-act-rowline__main">
        <div className="td-act-rowtitle">
          {single
            ? <ReviewLink part={parts[0]} label={paper.name} links={links} ariaLabel={`Review ${paper.name}`} />
            : paper.name}
        </div>
        <div className="td-act-rowsub">{meta}</div>
        {parts.length > 1 ? (
          <div className="td-act-rowsub td-pr-rowparts">
            {'Review: '}
            {parts.map((part, i) => {
              const label = part.part_label || `Part ${i + 1}`;
              return (
                <React.Fragment key={paperRefFor(part)}>
                  {i ? ' · ' : null}
                  <ReviewLink part={part} label={label} links={links} ariaLabel={`Review ${paper.name}, ${label}`} />
                </React.Fragment>
              );
            })}
          </div>
        ) : null}
      </div>
      <span className="td-act-mark">
        {score.figure === '—' ? score.figure : <>{score.figure} <small>correct</small></>}
      </span>
    </li>
  );
};

/**
 * A row's React key: the ids of the parts it is made of, which no other row
 * shares. Completion time and name are not unique: a sitting's parts handed
 * in together finish in the same second with the same name, and two rows
 * with one key make React keep stale rows across exam switches.
 */
export function paperRowKey(paper, index) {
  const refs = (paper.parts || []).map((part) => paperRefFor(part)).filter(Boolean);
  return refs.length ? refs.join('+') : `row-${index}-${paper.completed_at}-${paper.name}`;
}

const Papers = ({ papers, links }) => (
  <div className="td-card">
    <h4 className="td-card__title">Papers</h4>
    {papers.length ? (
      <ul className="td-act-rows">
        {papers.map((p, i) => <PaperRow key={paperRowKey(p, i)} paper={p} links={links} />)}
      </ul>
    ) : <p className="td-act-meta">No papers in this period.</p>}
  </div>
);

/** Timed papers whose week is on the chart, at their own accuracy. */
function timedMarkers(block) {
  const weekIndex = new Map(block.weekly_accuracy.map((w, i) => [w.week_start, i]));
  return block.papers
    .filter((p) => TIMED.has(p.mode) && p.accuracy != null)
    .map((p) => ({ index: weekIndex.get(weekStart(londonDateKey(p.completed_at))), value: p.accuracy }))
    .filter((m) => m.index != null);
}

function accuracySummary(block, markers) {
  const weeks = block.weekly_accuracy;
  const first = percent(weeks[0].accuracy);
  const last = percent(weeks[weeks.length - 1].accuracy);
  const span = weeks.length === 1
    ? `${last} in the week of ${dayMonth(weeks[0].week_start)}`
    : `from ${first} in the week of ${dayMonth(weeks[0].week_start)} to ${last} in the week of ${dayMonth(weeks[weeks.length - 1].week_start)}`;
  const timed = markers.length ? `, with ${plural(markers.length, 'timed paper')} marked` : '';
  return `${block.label} accuracy by week, ${span}${timed}`;
}

const Accuracy = ({ block }) => {
  const markers = timedMarkers(block);
  return (
    <div className="td-card">
      <h4 className="td-card__title">Accuracy over time</h4>
      {block.weekly_accuracy.length ? (
        <>
          <LineChart
            points={block.weekly_accuracy.map((w) => ({
              key: w.week_start, label: dayMonth(w.week_start).toUpperCase(), value: w.accuracy,
            }))}
            markers={markers}
            ariaLabel={accuracySummary(block, markers)}
            format={(v) => `${trimNumber(v, 0)}%`}
          />
          <div className="td-act-legend">
            <span className="td-act-legend__line">Weekly accuracy, all questions</span>
            {markers.length ? <span className="td-act-legend__marker">Timed paper</span> : null}
          </div>
        </>
      ) : <p className="td-act-meta">No questions answered in this period.</p>}
    </div>
  );
};

const Trend = ({ topic }) => {
  if (topic.accuracy_prev == null) return <span>—</span>;
  const diff = Math.round(topic.accuracy - topic.accuracy_prev);
  if (diff === 0) return <span>Same</span>;
  const up = diff > 0;
  return (
    <span className={up ? 'td-act-up' : 'td-act-down'}>
      <span aria-hidden="true">{`${up ? '▲' : '▼'} ${Math.abs(diff)}`}</span>
      <span className="td-act-sr">{`${up ? 'Up' : 'Down'} ${Math.abs(diff)} points`}</span>
    </span>
  );
};

/** "TMUA1.1" under TMUA reads "1.1"; any other code is shown as it is. */
const topicCode = (groupId, exam) => (groupId.startsWith(exam) ? groupId.slice(exam.length) : groupId);

const Topics = ({ block }) => (
  <div className="td-card td-act-gap">
    <h4 className="td-card__title">Topics</h4>
    {block.topics.length ? (
      <div className="td-act-scroll">
        <table className="td-act-topics">
          <thead>
            <tr>
              <th scope="col">Topic</th>
              <th scope="col">Qs</th>
              <th scope="col">Accuracy</th>
              <th scope="col">Trend</th>
            </tr>
          </thead>
          <tbody>
            {block.topics.map((t) => (
              <tr key={t.group_id}>
                <td>{`${topicCode(t.group_id, block.exam)} ${t.label}`}</td>
                <td className="td-act-num">{t.answered}</td>
                <td>
                  <span className="td-act-barnum">
                    <span className="td-act-bar" aria-hidden="true">
                      <i className={t.accuracy < 45 ? 'lo' : (t.accuracy < 70 ? 'mid' : '')} style={{ width: `${Math.min(100, t.accuracy)}%` }} />
                    </span>
                    <span className="td-act-num">{percent(t.accuracy)}</span>
                  </span>
                </td>
                <td><Trend topic={t} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : <p className="td-act-meta">No topic questions answered in this period.</p>}
    <div className="td-act-habits">
      <div className="td-act-habit">
        <span className="td-stat__eyebrow">Timed papers</span>
        <span className="td-act-habit__value">
          {block.habits.papers ? `${block.habits.timed_papers} of ${block.habits.papers}` : '—'}
        </span>
        <span className="td-act-habit__caption">
          {block.habits.papers ? 'papers sat under exam timing' : 'No papers in this period'}
        </span>
      </div>
    </div>
  </div>
);

/**
 * Exam practice, from GET …/activity/exams: one exam at a time (picked with
 * pressed buttons), its papers, weekly accuracy, topics and timed papers.
 * Only exams with practice in the period are sent. With `onOpenPaper(ref)`
 * (and `paperHref(ref)` for the links' href), each paper, or each part of a
 * sitting, links to its review.
 */
const ExamSection = ({ state, onOpenPaper, paperHref }) => {
  const links = onOpenPaper
    ? { open: onOpenPaper, href: paperHref || ((ref) => paperReviewHref(new URLSearchParams(), ref)) }
    : null;
  const [selected, setSelected] = useState(null);
  const exams = state.status === 'ready' ? state.data.exams : [];
  const current = exams.find((e) => e.exam === selected) || exams[0];

  return (
    <ActivitySection
      id="exams"
      title="Exam practice"
      description="Papers, practice topics and quiz questions, by exam. Only exams the student has attempted are shown."
    >
      {state.status === 'ready' || state.status === 'empty' ? (
        <ExamStatTiles summary={state.data && state.data.summary} empty={state.status === 'empty'} />
      ) : null}
      <SectionState state={state} what="exam practice" emptyTitle="No exam practice in this period" />
      {current ? (
        <>
          <ExamTable
            rows={(state.data.summary && state.data.summary.by_exam) || []}
            selected={current.exam}
            onSelect={setSelected}
          />
          <div className="td-act-toggle td-act-toggle--exams" role="group" aria-label="Exam">
            {exams.map((e) => (
              <button key={e.exam} type="button" aria-pressed={e === current} onClick={() => setSelected(e.exam)}>
                {e.label}
              </button>
            ))}
          </div>
          <div className="td-act-grid td-act-grid--12">
            {/* Keyed by exam: switching exam mounts a fresh list, never a reused one. */}
            <Papers key={current.exam} papers={current.papers} links={links} />
            <Accuracy block={current} />
          </div>
          <Topics block={current} />
        </>
      ) : null}
    </ActivitySection>
  );
};

export default ExamSection;
